import React, { useMemo, useState } from 'react';
import Formatters from '../components/grid-addons/formatters';
import { PriceExpansion } from './PriceExpansion';
import attributeQuery from '../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import { useWorkspace } from '../graphql/hooks/useWorkspace';
import Workspace from '../components/Workspace';
import { useAttributesForWorkspace } from '../components/Workspace/useAttributesForWorkspace';
import Fieldset from '../components/connectedForm/Fieldset';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { Toggle } from '@fluentui/react/lib/Toggle';
import Row from '../components/connectedForm/Row';
import { useId } from '@fluentui/react-hooks';
import {
  CostViewPermission,
  FilterOperation,
  ICustomerPriceWork,
  IFilterInput,
  ProductClass,
} from '../graphql/schema/p4types';
import useWarehouseColumns from '../utils/useWarehouseColumns';
import { useSelector } from 'react-redux';
import { usePrecision } from '../utils/hooks';
import { IEditableDataGridColumnDef } from '../@types/editableDataGridTypes';
import { ExpansionUUIDProvider } from '../components/Context/ExpansionUUIDProvider';
import { IReduxStore } from '../@types/reduxTypes';
import ProductFormModalFormatter from '../components/modals/formatters/ProductFormModalFormatter';

const PRICE_LIST_WAREHOUSE_DETAIL_FIELDS = [
  {
    title: 'Free Stock',
    fieldName: 'freeStock',
  },
  {
    title: 'On Order',
    fieldName: 'onOrder',
  },
  {
    title: 'Makeable',
    fieldName: 'makeable',
  },
];

const noFreeTypeFilter: IFilterInput = {
  filterOperation: FilterOperation.NotEquals,
  filterParameters: {
    field: 'product.productClass',
    value: ProductClass.FreeType,
  },
};

const availableFiltersFilter = (column: IEditableDataGridColumnDef) => {
  return column?.fieldName?.includes('product');
};
const availableColumnsFilter = (column: IEditableDataGridColumnDef) => {
  //Exclude subselections of child list, not valid columns.
  return column?.fieldName && !column.fieldName.includes('pricePotentialList.');
};

export default (props) => {
  const { workspace, type, title } = props.entity;
  const { fieldNames = [] } = props;

  const supplierToggleTooltipId = useId('supplierToggleTooltipId');
  const [supplierToggle, setSupplierToggle] = useState(false);
  const costPricePrecision = usePrecision('cost');

  const filters: Array<IFilterInput> = useMemo(
    () =>
      supplierToggle
        ? [
            {
              filterOperation: FilterOperation.Equals,
              filterParameters: {
                field: 'product.mainSupplierDetails.supplier.id',
                value: props.supplierId,
              },
            },
            noFreeTypeFilter,
          ]
        : [noFreeTypeFilter],
    [props.supplierId, supplierToggle]
  );

  const costPermission = useSelector(
    (store: IReduxStore) => store.app?.fullUserDetails?.companyUser?.costViewPermission
  );

  const columnOverrides = useMemo(() => {
    return {
      ...(props.columnOverrides || {}),
      pricePotentialList: {
        name: 'Potential Prices',
        formatter: Formatters.ExpansionFormatter,
        options: {
          formatter: Formatters.TextFieldFormatter,
          displayKey: 'nettPrice',
        },
      },
      'product.reference': {
        name: 'Product',
        formatter: ProductFormModalFormatter,
        options: {
          modalProps: { forceReadOnly: true },
          entityId: 'product.id',
        },
      },
      'product.vatProductCategory.description': {
        name: 'VAT Product Category - Description',
      },
      'product.mainSupplierDetails.supplier.reference': {
        sortable: false,
      },
      ...(costPermission === CostViewPermission.None
        ? {
            'product.latestCostPrice': { disabled: true },
            'product.costUnitDescription': { disabled: true },
          }
        : {
            'product.latestCostPrice': {
              options: { format: { decimals: costPricePrecision } },
            },
          }),
    };
  }, [props.columnOverrides, costPermission, costPricePrecision]);

  const columnOverrider = (column: { isTransient: boolean; fieldName: string | string[] }) => {
    column.isTransient = !column.fieldName || !column.fieldName.includes('product.');
    return column;
  };

  const warehouseColumns = useWarehouseColumns('product.productWarehouseDetails', PRICE_LIST_WAREHOUSE_DETAIL_FIELDS);

  const productAttributeColumns = useAttributesForWorkspace(attributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(() => {
    return { ...(props.customColumns || {}), ...warehouseColumns, ...productAttributeColumns };
  }, [props.customColumns, warehouseColumns, productAttributeColumns]);

  const workspaceProps = useWorkspace<ICustomerPriceWork>(props, props.entity, {
    columns: fieldNames,
    customColumns: customColumns,
    columnOverrides: columnOverrides,
    initialSortColumn: 'id', //Think this will either be ignored or effectively be against the product.
    filters: filters,
    additionalVariables: props.additionalVariables,
  });

  return (
    <ExpansionUUIDProvider>
      <Workspace
        entityType={type}
        title={title}
        defaultQuery={workspace}
        expandableRows={true}
        columnOverrider={columnOverrider}
        availableFiltersFilter={availableFiltersFilter}
        availableColumnsFilter={availableColumnsFilter}
        rowRenderer={PriceExpansion}
        childrenHeight={10}
        enableRowSelect={false}
        {...workspaceProps}
        {...props}
      >
        {props?.includeSupplierToggle ? (
          <div style={{ padding: '1em', background: 'white', border: '1px #e0e0e0 solid' }}>
            <Row>
              <Fieldset cols={1 / 4}>
                <TooltipHost
                  content="Choose whether to only show products which have a main supplier matching the supplier on the purchase order"
                  id={supplierToggleTooltipId}
                >
                  <Toggle
                    defaultChecked={false}
                    onText="Only show matching main supplier"
                    offText="Show all products"
                    onChange={(event, checked) => {
                      setSupplierToggle(!!checked);
                    }}
                    aria-describedby={supplierToggleTooltipId}
                  />
                </TooltipHost>
              </Fieldset>
            </Row>
          </div>
        ) : null}
      </Workspace>
    </ExpansionUUIDProvider>
  );
};
